// Keyboard state belongs to each navbar search. Read the current DOM on
// every keypress, so a patched search cannot activate an old result token.
export const SearchKeyboard = {
  mounted() {
    this.window = this.el.ownerDocument.defaultView
    this.setActionsHidden(false)
    this.onGlobalKeydown = (event) => {
      this.setActionsHidden(event.key === "Alt" || !!event.altKey)
      if (
        this.el.dataset.spotlightEnabled !== "true" ||
        this.window.innerWidth < 640 ||
        event.defaultPrevented ||
        event.isComposing ||
        event.repeat ||
        event.altKey
      )
        return
      // `/` is matched by character, so layouts that type it with Shift
      // (Czech, German) work; it never fires while someone is typing.
      const slash =
        event.key === "/" &&
        !event.metaKey &&
        !event.ctrlKey &&
        !isEditable(event.target)
      const commandK =
        !event.shiftKey &&
        (event.metaKey || event.ctrlKey) &&
        event.key.toLowerCase() === "k"
      if (!slash && !commandK) return

      event.preventDefault()
      // Let an in-progress quick action or email draft keep its focus and
      // unsaved changes. Both can live outside this root (the /search page
      // renders its action dialog beside its own keyboard root), and only
      // one email composer can be mounted at a time.
      if (
        this.el.ownerDocument.querySelector(
          '[data-search-action-open="true"], [data-email-composer]',
        )
      )
        return
      if (slash) this.focusSearchField()
      else this.openSpotlight()
    }
    this.onGlobalKeyup = (event) => this.setActionsHidden(!!event.altKey)
    this.onWindowBlur = () => this.setActionsHidden(false)

    this.onKeydown = (event) => {
      const isArrow = event.key === "ArrowDown" || event.key === "ArrowUp"
      if (
        event.defaultPrevented ||
        event.isComposing ||
        event.repeat ||
        event.metaKey ||
        event.ctrlKey ||
        (event.altKey && !isArrow)
      )
        return
      const input = event.target.closest("[data-search-input]")
      const focused = event.target.closest(
        "[data-search-result], [data-search-action]",
      )
      if (!input && !focused) return
      if (isArrow) this.setActionsHidden(!!event.altKey)
      const list = this.el.querySelector("[data-search-result-list]")
      // While the debounce is pending, Enter must search the new query.
      if (!list || (input && input.value !== list.dataset.searchQuery)) return
      const items = Array.from(
        list.querySelectorAll("[data-search-result], [data-search-action]"),
      ).filter((item) => !item.disabled && item.getClientRects().length)
      if (!items.length) return
      const firstResult = items.find((item) =>
        item.matches("[data-search-result]"),
      )
      if (event.key === "Enter" && input) {
        if (!firstResult) return
        event.preventDefault()
        firstResult.click()
      } else if (event.key === "Tab" && input && !event.shiftKey) {
        if (!firstResult) return
        event.preventDefault()
        const firstAction = firstResult
          .closest("[data-search-result-group]")
          .querySelector("[data-search-action]")
        ;(items.includes(firstAction) ? firstAction : firstResult).focus()
      } else if (isArrow) {
        // Arrows move between whole rows; Tab reaches a row's actions.
        const rows = items.filter((item) =>
          item.matches("[data-search-result]"),
        )
        if (!rows.length) return
        event.preventDefault()
        const direction = event.key === "ArrowDown" ? 1 : -1
        const position = rows.indexOf(
          focused
            ?.closest("[data-search-result-group]")
            ?.querySelector("[data-search-result]"),
        )
        const index =
          input || position === -1
            ? direction === 1
              ? 0
              : rows.length - 1
            : (position + direction + rows.length) % rows.length
        rows[index].focus()
      }
    }
    this.window.addEventListener("keydown", this.onGlobalKeydown)
    this.window.addEventListener("keyup", this.onGlobalKeyup)
    this.window.addEventListener("blur", this.onWindowBlur)
    this.el.addEventListener("keydown", this.onKeydown)
  },

  updated() {
    this.setActionsHidden(this.actionsHidden)
  },

  // Cmd+K: Spotlight, carrying over whatever the navbar field holds.
  openSpotlight() {
    const input = this.el.querySelector(
      "[data-search-spotlight] [data-search-input]",
    )
    if (input) {
      input.focus()
      input.select()
    } else {
      const query = this.el.querySelector("[data-search-input]")?.value
      this.pushEventTo(
        this.el,
        "search:spotlight:open",
        query === undefined ? {} : { query },
      )
    }
  },

  // `/`: the search field already on screen — an open Spotlight, the /search
  // page, or the navbar field — expanding the collapsed navbar field when it
  // is behind its icon, and opening Spotlight where the page shows none.
  focusSearchField() {
    const doc = this.el.ownerDocument
    const field = [
      this.el.querySelector("[data-search-spotlight] [data-search-input]"),
      doc.querySelector("[data-search-page-input]"),
      this.el.querySelector("[data-search-inline]"),
    ].find((el) => el?.getClientRects().length)
    if (field) {
      field.focus()
      field.select()
      return
    }
    const trigger = doc.querySelector("[data-search-open-trigger]")
    if (trigger?.getClientRects().length) trigger.click()
    else this.openSpotlight()
  },

  setActionsHidden(hidden) {
    if (hidden) {
      this.el
        .querySelector("[data-search-action]:focus")
        ?.closest("[data-search-result-group]")
        .querySelector("[data-search-result]")
        ?.focus({ preventScroll: true })
    }
    this.actionsHidden = hidden
    this.el.dataset.searchActionsHidden = String(hidden)
  },

  destroyed() {
    this.window.removeEventListener("keydown", this.onGlobalKeydown)
    this.window.removeEventListener("keyup", this.onGlobalKeyup)
    this.window.removeEventListener("blur", this.onWindowBlur)
    this.el.removeEventListener("keydown", this.onKeydown)
  },
}

// Mount with the Spotlight form itself; parent hook updates are not needed
// to discover the newly inserted input.
export const SearchAutofocus = {
  mounted() {
    this.window = this.el.ownerDocument.defaultView
    const input = this.el.querySelector("[data-search-input]")
    // The native dialog mounts while its animated sheet is still display:none.
    const focusWhenVisible = () => {
      if (!input.isConnected) return
      if (!input.getClientRects().length || !input.closest("dialog").open) {
        this.focusFrame = this.window.requestAnimationFrame(focusWhenVisible)
        return
      }
      input.focus()
      input.select()
    }
    focusWhenVisible()
  },

  destroyed() {
    this.window.cancelAnimationFrame(this.focusFrame)
  },
}

function isEditable(target) {
  if (!target || typeof target.closest !== "function") return false
  return Boolean(
    target.isContentEditable ||
    target.closest(
      "input, textarea, select, [contenteditable]:not([contenteditable='false'])",
    ),
  )
}
